import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { BonusPaymentTableService } from "services/bonus-payment-table";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { numberToWords, getPrintTemplate } from "./components/printTemplate";

const createRow = () => ({
  fullName: "",
  rank: "",
  bonusType: "",
  amount: "",
  signature: "",
  note: "",
});

const roundMoney = (value) => {
  const parsed = parseVndIntegerInput(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : 0;
};

const fmt = (n) => new Intl.NumberFormat("vi-VN").format(roundMoney(n));

function BonusPaymentTableDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();
  const quarterNow = Math.floor(today.getMonth() / 3) + 1;

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [header, setHeader] = useState({
    unit: "",
    department: "",
    docNumber: "",
    quarter: String(quarterNow),
    year: String(today.getFullYear()),
  });
  const [rows, setRows] = useState([createRow()]);

  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        const response = await BonusPaymentTableService.getById(id);
        const apiData = response.data ? response.data : response;

        setHeader({
          unit: apiData.unit || "",
          department: apiData.department || "",
          docNumber: apiData.unitNumber || "",
          quarter: apiData.quarter || String(quarterNow),
          year: apiData.year || String(today.getFullYear()),
        });

        const mappedRows = (apiData.employeeBonuses || []).map((emp) => ({
          fullName: emp.name || "",
          rank: emp.salaryLevel || "",
          bonusType: emp.bonusType || "",
          amount: formatVndIntegerInput(emp.amount || ""),
          signature: emp.signature || "",
          note: emp.note || "",
        }));

        setRows(mappedRows.length > 0 ? mappedRows : [createRow()]);
      } catch (error) {
        toast.error("Không thể tải dữ liệu bảng thưởng");
      }
    })();
  }, [id]);

  const totalBonus = useMemo(() => rows.reduce((sum, r) => sum + roundMoney(r.amount), 0), [rows]);

  const amountInWords = useMemo(() => numberToWords(totalBonus), [totalBonus]);

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setRowField = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      if (field === "amount") {
        next[index] = { ...next[index], [field]: formatVndIntegerInput(value) };
        return next;
      }
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, createRow()]);
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildPayload = () => {
    const year = String(header.year || "").trim();
    const quarter = String(header.quarter || "").trim();
    const unitNumber = String(header.docNumber || "").trim();
    const unit = String(header.unit || "").trim();
    const department = String(header.department || "").trim();

    if (!/^\d{4}$/.test(year)) {
      throw new Error("Năm phải gồm 4 chữ số (ví dụ: 2026)");
    }
    if (!["1", "2", "3", "4"].includes(quarter)) {
      throw new Error("Quý không hợp lệ (chỉ từ 1 đến 4)");
    }
    if (!unitNumber) {
      throw new Error("Số chứng từ (Số) không được để trống");
    }
    if (!unit) {
      throw new Error("Đơn vị không được để trống");
    }
    if (!department) {
      throw new Error("Bộ phận không được để trống");
    }

    const normalizedRows = rows.map((item) => ({
      fullName: item.fullName?.trim() || "",
      rank: item.rank?.trim() || "",
      bonusType: item.bonusType?.trim() || "",
      amount: item.amount === "" ? "" : roundMoney(item.amount),
      signature: item.signature?.trim() || "",
      note: item.note?.trim() || "",
    }));

    const nonEmptyRows = normalizedRows.filter(
      (item) =>
        item.fullName ||
        item.rank ||
        item.bonusType ||
        item.amount !== "" ||
        item.signature ||
        item.note
    );

    if (!nonEmptyRows.length) {
      throw new Error("Bảng thưởng phải có ít nhất 1 nhân viên");
    }

    nonEmptyRows.forEach((item, index) => {
      if (!item.fullName || !item.rank || !item.bonusType || item.amount === "") {
        throw new Error(`Dòng ${index + 1} chưa nhập đủ Họ tên, Ngạch bậc, Loại thưởng, Số tiền`);
      }
      if (Number(item.amount) <= 0) {
        throw new Error(`Dòng ${index + 1} phải có Số tiền > 0`);
      }
    });

    const employees = nonEmptyRows.map((item) => ({
      name: item.fullName,
      salaryLevel: item.rank,
      bonusType: item.bonusType,
      amount: roundMoney(item.amount),
      signature: item.signature,
      note: item.note,
    }));

    return {
      year,
      quarter,
      unitNumber,
      unit,
      department,
      totalBonus: String(employees.reduce((sum, item) => sum + roundMoney(item.amount), 0)),
      totalEmployment: String(employees.length),
      employees,
    };
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await BonusPaymentTableService.create(payload);
        toast.success("Tạo bảng thưởng thành công!");
      } else {
        await BonusPaymentTableService.update(id, payload);
        toast.success("Cập nhật bảng thưởng thành công!");
      }
      navigation("/templates/bonus-payment-table");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      headerInfo: header,
      rows,
      totalBonus,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "14px",
    },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "20px" },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "250px",
      fontSize: "14px",
      fontFamily: "inherit",
      fontWeight: "bold",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    inputDottedSmall: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "100px",
      fontSize: "14px",
      fontFamily: "inherit",
      textAlign: "center",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    titleBox: { textAlign: "center", margin: "20px 0" },
    titleMain: { fontSize: "20px", fontWeight: "bold", margin: 0, textTransform: "uppercase" },
    tableWrap: { overflowX: "auto", border: "1px solid #ccc" },
    table: { width: "100%", borderCollapse: "collapse", minWidth: 1100, fontSize: "12px" },
    th: {
      border: "1px solid #000",
      padding: "4px",
      textAlign: "center",
      fontWeight: "bold",
      verticalAlign: "middle",
      height: "40px",
    },
    td: { border: "1px solid #000", padding: 0, height: "30px" },
    inputTable: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    inputTableText: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    inputTableNumber: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item xs={12} lg={12}>
            <Card sx={{ height: "100%" }}>
              <SoftBox p={2} style={s.container}>
                <div style={s.headerTop}>
                  <div style={{ width: "60%" }}>
                    <div style={{ fontWeight: "bold", marginBottom: 5 }}>
                      Đơn vị:{" "}
                      <input
                        disabled={containView}
                        style={s.inputDotted}
                        value={header.unit}
                        onChange={(e) => setHeaderField("unit", e.target.value)}
                      />
                    </div>
                    <div style={{ fontWeight: "bold" }}>
                      Bộ phận:{" "}
                      <input
                        disabled={containView}
                        style={s.inputDotted}
                        value={header.department}
                        onChange={(e) => setHeaderField("department", e.target.value)}
                      />
                    </div>
                  </div>
                  <div style={{ textAlign: "center", width: "40%" }}>
                    <div style={{ fontWeight: "bold" }}>Mẫu số: 02 - LĐTL</div>
                    <div style={{ fontStyle: "italic", fontSize: "12px", marginTop: 4 }}>
                      (Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ
                      Tài chính)
                    </div>
                  </div>
                </div>

                <div style={s.titleBox}>
                  <h2 style={s.titleMain}>BẢNG THANH TOÁN TIỀN THƯỞNG</h2>
                  <div style={{ fontStyle: "italic", marginTop: 5 }}>
                    Quý{" "}
                    <input
                      disabled={containView}
                      type="text"
                      inputMode="numeric"
                      min={1}
                      max={4}
                      style={{ ...s.inputDottedSmall, width: "40px" }}
                      value={header.quarter}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          setHeaderField("quarter", "");
                          return;
                        }
                        const num = Number(value);
                        if (num >= 1 && num <= 4) {
                          setHeaderField("quarter", String(num));
                        }
                      }}
                    />{" "}
                    năm{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDottedSmall, width: "70px" }}
                      value={header.year}
                      onChange={(e) => setHeaderField("year", e.target.value)}
                    />
                  </div>
                  <div style={{ textAlign: "right", marginTop: 10 }}>
                    Số:{" "}
                    <input
                      disabled={containView}
                      style={s.inputDottedSmall}
                      value={header.docNumber}
                      onChange={(e) => setHeaderField("docNumber", e.target.value)}
                    />
                  </div>
                </div>

                <div style={s.tableWrap}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th rowSpan={2} style={s.th}>STT</th>
                        <th rowSpan={2} style={s.th}>Họ và tên</th>
                        <th rowSpan={2} style={s.th}>Ngạch bậc lương hoặc cấp bậc chức vụ</th>
                        <th colSpan={2} style={s.th}>Mức thưởng</th>
                        <th rowSpan={2} style={s.th}>Ký nhận</th>
                        <th rowSpan={2} style={s.th}>Ghi chú</th>
                        {!containView && (
                          <th rowSpan={2} style={{ ...s.th, width: "30px" }}>#</th>
                        )}
                      </tr>
                      <tr>
                        <th style={s.th}>Xếp loại thưởng</th>
                        <th style={s.th}>Số tiền</th>
                      </tr>
                      <tr style={{ backgroundColor: "#fafafa" }}>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>C</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>2</th>
                        <th style={s.th}>D</th>
                        <th style={s.th}>E</th>
                        {!containView && <th style={s.th}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={`row-${index}`}>
                          <td style={{ ...s.td, textAlign: "center" }}>{index + 1}</td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputTableText}
                              value={row.fullName}
                              onChange={(e) => setRowField(index, "fullName", e.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={{ ...s.inputTableText, textAlign: "center" }}
                              value={row.rank}
                              onChange={(e) => setRowField(index, "rank", e.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={{ ...s.inputTable, textAlign: "center" }}
                              value={row.bonusType}
                              onChange={(e) => setRowField(index, "bonusType", e.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            {containView ? (
                              <span style={{ ...s.inputTableNumber, lineHeight: "30px" }}>
                                {fmt(row.amount)}
                              </span>
                            ) : (
                              <input
                                type="text"
                                inputMode="numeric"
                                style={s.inputTableNumber}
                                value={row.amount}
                                onChange={(e) => setRowField(index, "amount", e.target.value)}
                              />
                            )}
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={{ ...s.inputTableText, textAlign: "center" }}
                              value={row.signature}
                              onChange={(e) => setRowField(index, "signature", e.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputTableText}
                              value={row.note}
                              onChange={(e) => setRowField(index, "note", e.target.value)}
                            />
                          </td>
                          {!containView && (
                            <td style={s.td}>
                              <button
                                type="button"
                                onClick={() => handleRemoveRow(index)}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: "red",
                                  fontWeight: "bold",
                                  cursor: "pointer",
                                  width: "100%",
                                  height: "100%",
                                }}
                              >
                                X
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}

                      <tr>
                        <td colSpan={4} style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>
                          Cộng
                        </td>
                        <td style={{ ...s.td, textAlign: "right", fontWeight: 700, paddingRight: 10 }}>
                          {fmt(totalBonus)}
                        </td>
                        <td style={s.td} />
                        <td style={s.td} />
                        {!containView && <td style={s.td} />}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <SoftBox mt={2} fontStyle="italic">
                  Tổng số tiền (Viết bằng chữ): <strong>{amountInWords}</strong>
                </SoftBox>

                <SoftBox mt={3} display="flex" gap={2} justifyContent="space-between">
                  <SoftButton variant="gradient" color="light" onClick={() => navigation(-1)}>
                    Trở về
                  </SoftButton>

                  <SoftBox display="flex" gap={2}>
                    {!containView && (
                      <SoftButton variant="outlined" color="dark" onClick={handleAddRow}>
                        THÊM DÒNG
                      </SoftButton>
                    )}

                    {containView ? (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In Bảng Thưởng
                      </SoftButton>
                    ) : (
                      <SoftButton
                        variant="gradient"
                        color="dark"
                        onClick={handleSubmit}
                        disabled={loadingSubmit}
                      >
                        {id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default BonusPaymentTableDetail;
